<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { PATHS } from '@/content'

const props = defineProps<{ servicePath?: string; subjectDefault?: string }>()
const router = useRouter()

const API = (import.meta.env.VITE_API_URL as string) || ''

const subjects = [
  'Demande de devis / étude',
  'Dépannage urgent',
  'Menuiserie (portes, fenêtres, volets)',
  'Agencement sur mesure',
  'Isolation',
  'Couverture',
  'Ossature bois',
  'Entretien',
  'Projet professionnel',
  'Autre demande',
]

const form = reactive({
  firstname: '',
  lastname: '',
  email: '',
  address: '',
  zip: '',
  city: '',
  phone: '',
  subject: props.subjectDefault || '',
  message: '',
  website: '', // honeypot
})

const state = ref<'idle' | 'sending' | 'error'>('idle')
const errorMsg = ref('')

async function submit() {
  errorMsg.value = ''
  // Honeypot rempli -> bot : on simule un succes sans envoyer.
  if (form.website) {
    router.push(PATHS.merci)
    return
  }
  state.value = 'sending'
  const payload = {
    name: `${form.firstname} ${form.lastname}`.trim(),
    email: form.email,
    phone: form.phone,
    subject: form.subject || 'Demande de contact',
    message: [
      form.message,
      '',
      `Adresse : ${form.address} ${form.zip} ${form.city}`.trim(),
    ]
      .filter(Boolean)
      .join('\n'),
    service_path: props.servicePath || '',
    website: form.website,
  }
  try {
    const res = await fetch(`${API}/api/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    router.push(PATHS.merci)
  } catch {
    state.value = 'error'
    errorMsg.value =
      "L'envoi a échoué. Merci de réessayer ou de nous appeler directement, nous restons à votre écoute."
  }
}
</script>

<template>
  <form class="cform" @submit.prevent="submit">
    <div class="cform__row">
      <label class="cform__field">
        <span>Prénom <b>*</b></span>
        <input v-model="form.firstname" type="text" required autocomplete="given-name" />
      </label>
      <label class="cform__field">
        <span>Nom <b>*</b></span>
        <input v-model="form.lastname" type="text" required autocomplete="family-name" />
      </label>
    </div>

    <label class="cform__field">
      <span>Email <b>*</b></span>
      <input v-model="form.email" type="email" required autocomplete="email" />
    </label>

    <label class="cform__field">
      <span>Adresse</span>
      <input v-model="form.address" type="text" autocomplete="street-address" />
    </label>

    <div class="cform__row">
      <label class="cform__field">
        <span>Code postal <b>*</b></span>
        <input
          v-model="form.zip"
          type="text"
          required
          inputmode="numeric"
          pattern="[0-9]{5}"
          maxlength="5"
          title="5 chiffres, ex. 53100"
          autocomplete="postal-code"
        />
      </label>
      <label class="cform__field">
        <span>Ville <b>*</b></span>
        <input v-model="form.city" type="text" required autocomplete="address-level2" />
      </label>
    </div>

    <label class="cform__field">
      <span>Téléphone <b>*</b></span>
      <input v-model="form.phone" type="tel" required autocomplete="tel" />
    </label>

    <label class="cform__field">
      <span>Ma demande <b>*</b></span>
      <select v-model="form.subject" required>
        <option value="" disabled>- Choisir -</option>
        <option v-for="s in subjects" :key="s" :value="s">{{ s }}</option>
      </select>
    </label>

    <label class="cform__field">
      <span>Votre demande en quelques mots</span>
      <textarea v-model="form.message" rows="5"></textarea>
    </label>

    <!-- Honeypot anti-spam : ne pas remplir -->
    <div class="cform__hp" aria-hidden="true">
      <label>Ne pas remplir<input v-model="form.website" type="text" tabindex="-1" autocomplete="off" /></label>
    </div>

    <p v-if="state === 'error'" class="cform__error">{{ errorMsg }}</p>

    <button type="submit" class="btn btn--primary btn--block" :disabled="state === 'sending'">
      {{ state === 'sending' ? 'Envoi en cours…' : 'Envoyer ma demande' }}
    </button>
    <p class="cform__legal">
      Les champs marqués d'un <b>*</b> sont obligatoires. Vos données servent uniquement à traiter
      votre demande.
    </p>
  </form>
</template>

<style scoped>
.cform {
  display: grid;
  gap: 16px;
}
.cform__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.cform__field {
  display: grid;
  gap: 6px;
}
.cform__field > span {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--title);
}
.cform__field b {
  color: var(--green-dark);
}
input,
select,
textarea {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-family: inherit;
  font-size: 0.95rem;
  color: var(--ink);
  background: #fff;
}
input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: var(--green);
  box-shadow: 0 0 0 3px var(--green-soft);
}
textarea {
  resize: vertical;
}
.cform__hp {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
.cform__error {
  background: #fdecec;
  border: 1px solid #f5b5b5;
  color: #a12626;
  padding: 12px 14px;
  border-radius: var(--radius-sm);
  margin: 0;
  font-size: 0.9rem;
}
.cform__legal {
  font-size: 0.78rem;
  color: var(--muted);
  margin: 0;
}
@media (max-width: 560px) {
  .cform__row {
    grid-template-columns: 1fr;
  }
}
</style>
